import { createContext, useContext, useMemo, type ReactNode } from "react";

import { DEFAULT_LOCALE, type Locale } from "./config";
import { translate, type TVars } from "./dictionary";
import { formatters } from "./format";

type I18nValue = {
  locale: Locale;
  t: (ns: string, key: string, defaultText?: string, vars?: TVars) => string;
};

const I18nContext = createContext<I18nValue>({
  locale: DEFAULT_LOCALE,
  t: (ns, key, defaultText, vars) => translate(DEFAULT_LOCALE, ns, key, defaultText, vars),
});

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const value = useMemo<I18nValue>(
    () => ({
      locale,
      t: (ns, key, defaultText, vars) => translate(locale, ns, key, defaultText, vars),
    }),
    [locale],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useLocale(): Locale {
  return useContext(I18nContext).locale;
}

/**
 * Namespace-scoped translator.
 *
 *   const { t } = useTranslation("navigation");
 *   t("browse", "Browse");
 *   t("bids", "{count, plural, one {# bid} other {# bids}}", { count: 3 });
 */
export function useTranslation(ns: string) {
  const { locale, t } = useContext(I18nContext);
  return useMemo(
    () => ({
      locale,
      t: (key: string, defaultText?: string, vars?: TVars) => t(ns, key, defaultText, vars),
      ...formatters(locale),
    }),
    [locale, ns, t],
  );
}

export { formatters };
